import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import yongyuanLogo from "../../image/yongyuan.png";
import socialFacebook from "../../image/social/facebook.webp";
import socialYoutube from "../../image/social/youtube.webp";
import socialXiaohongshu from "../../image/social/xiaohongshu.webp";
import socialDouyin from "../../image/social/douyin.webp";
import { useLocale } from "../../i18n/LocaleContext";
import { isDomesticLocale } from "../../i18n/languages";
import { useLocalePath } from "../../i18n/useLocalePath";
import {
  getFooterWhatsappContacts,
  footerApplications,
  footerProducts,
} from "../../data/footerLinks";
import { useActiveEquipmentCategory } from "../../i18n/ActiveEquipmentCategoryContext";
import "./index.css";

const FOOTER_SOCIAL_QRS = [
  { id: "facebook", labelKey: "footerSocialFacebook", image: socialFacebook },
  { id: "youtube", labelKey: "footerSocialYoutube", image: socialYoutube },
  {
    id: "xiaohongshu",
    labelKey: "footerSocialXiaohongshu",
    image: socialXiaohongshu,
  },
  { id: "douyin", labelKey: "footerSocialDouyin", image: socialDouyin },
];

const LONG_PRESS_MS = 550;

async function downloadSocialQr(imageUrl, fileName) {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(imageUrl, "_blank", "noopener,noreferrer");
  }
}

function SocialQrItem({ item, label, saveHint, saveAria }) {
  const timerRef = useRef(null);
  const triggeredRef = useRef(false);

  const clearPressTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => () => clearPressTimer(), []);

  const startPress = (event) => {
    // 触屏交给系统长按「存储图像」；桌面端长按触发下载
    if (event.pointerType === "touch") return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    triggeredRef.current = false;
    clearPressTimer();
    timerRef.current = window.setTimeout(() => {
      triggeredRef.current = true;
      downloadSocialQr(item.image, `${item.id}-qr.webp`);
    }, LONG_PRESS_MS);
  };

  const endPress = () => {
    clearPressTimer();
  };

  const handleContextMenu = (event) => {
    // 保留系统长按/右键菜单，方便手机原生「存储图像」
    clearPressTimer();
  };

  return (
    <li className="site-common-footer-social-item">
      <div
        className="site-common-footer-social-qr"
        role="img"
        aria-label={`${saveAria} ${label}`}
        title={saveHint}
        onPointerDown={startPress}
        onPointerUp={endPress}
        onPointerLeave={endPress}
        onPointerCancel={endPress}
        onContextMenu={handleContextMenu}
      >
        <img src={item.image} alt={label} />
      </div>
      <span className="site-common-footer-social-label">{label}</span>
    </li>
  );
}

function Footer() {
  const { t, language } = useLocale();
  const localePath = useLocalePath();
  const isDomestic = isDomesticLocale(language);
  const { activeCategory } = useActiveEquipmentCategory();
  const whatsappContacts = getFooterWhatsappContacts(activeCategory);

  return (
    <footer className="site-common-footer">
      <div className="site-common-container site-common-footer-main">
        <div className="site-common-footer-grid">
          <div className="site-common-footer-brand">
            <Link to={localePath("/")} className="site-common-footer-logo-link">
              <img
                src={yongyuanLogo}
                alt="YONGYUAN"
                className="site-common-footer-logo"
              />
            </Link>
            <p className="site-common-footer-tagline">{t("footerTagline")}</p>
            <p className="site-common-footer-reach">{t("footerReach")}</p>

            <div className="site-common-footer-social">
              <p className="site-common-footer-social-title">
                {t("footerSocialTitle")}
              </p>
              <p className="site-common-footer-social-hint">
                {t("footerSocialSaveHint")}
              </p>
              <ul className="site-common-footer-social-list">
                {FOOTER_SOCIAL_QRS.map((item) => (
                  <SocialQrItem
                    key={item.id}
                    item={item}
                    label={t(item.labelKey)}
                    saveHint={t("footerSocialSaveHint")}
                    saveAria={t("footerSocialSaveAria")}
                  />
                ))}
              </ul>
            </div>
          </div>

          <div className="site-common-footer-col">
            <h3>{t("footerProductsTitle")}</h3>
            <ul>
              {footerProducts.map((item) => (
                <li key={item.labelKey}>
                  <Link
                    to={`${localePath("/products")}?category=${item.category}`}
                  >
                    {t(item.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="site-common-footer-col">
            <h3>{t("footerApplicationsTitle")}</h3>
            <ul>
              {footerApplications.map((item) => (
                <li key={item.labelKey}>
                  <Link to={localePath("/cases")}>{t(item.labelKey)}</Link>
                </li>
              ))}
              <li>
                <Link to={localePath("/cases")}>......</Link>
              </li>
            </ul>
          </div>

          <div
            id="footer-contact"
            className="site-common-footer-col site-common-footer-contact"
          >
            <h3>{t("footerContactTitle")}</h3>
            <ul className="site-common-footer-contact-list">
              <li>
                <span className="site-common-footer-contact-label">
                  {t("footerWhatsapp")}
                </span>
                <div className="site-common-footer-whatsapp-list">
                  {whatsappContacts.map((item) => (
                    <a
                      key={item.labelKey}
                      href={item.href}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {t(item.labelKey)}
                    </a>
                  ))}
                </div>
              </li>
              <li>
                <span className="site-common-footer-contact-label">
                  {t("footerAddress")}
                </span>
                <span>{t("footerAddressValue")}</span>
              </li>
              <li>
                <span className="site-common-footer-contact-label">
                  {t("footerAddressEast")}
                </span>
                <span>{t("footerAddressEastValue")}</span>
              </li>
              <li>
                <span className="site-common-footer-contact-label">
                  {t("footerAddressVietnam")}
                </span>
                <span>{t("footerAddressVietnamValue")}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="site-common-container site-common-footer-bottom">
        <p className="site-common-copyright">{t("copyright")}</p>
        {isDomestic ? (
          <p className="site-common-copyright">
            <a
              className="site-common-beian"
              href="https://beian.miit.gov.cn"
              target="_blank"
              rel="noreferrer"
            >
              粤ICP备2026025113号-1
            </a>
          </p>
        ) : null}
      </div>
    </footer>
  );
}

export default Footer;
